import { useCallback, useRef, useState } from 'react';
import type {
  InvestigationReportData,
  InvestigationRunStatus,
  InvestigationStreamMessage,
  InvestigationThought,
  InvestigationToolCall,
  StartInvestigationRequest,
  StoredInvestigationRun,
} from '../types';
import { getSreInvestigationMode, SSE_EVENT } from '../constants';
import {
  cancelInvestigation,
  createInvestigationRunId,
  getInvestigation,
  mapStoredToolCalls,
  runDemoInvestigationStream,
  startLiveInvestigationStream,
} from '../services/sreInvestigationService';
import { normalizeInvestigationReport } from '../utils/normalizeReport';

export interface UseSreInvestigationStreamOptions {
  onComplete?: (report: InvestigationReportData | null, markdown: string) => void;
  onError?: (error: string) => void;
}

export interface UseSreInvestigationStreamResult {
  runId: string | null;
  status: InvestigationRunStatus;
  isStreaming: boolean;
  error: string | null;
  messages: InvestigationStreamMessage[];
  toolCalls: InvestigationToolCall[];
  thoughts: InvestigationThought[];
  report: InvestigationReportData | null;
  conclusionMarkdown: string | null;
  startedAt: Date | null;
  completedAt: Date | null;
  triggerSource: string;
  startInvestigation: (request: StartInvestigationRequest) => Promise<void>;
  /** Load a persisted pulse-sre run into the workbench (no SSE). */
  hydrateFromStoredRun: (runIdOrRun: string | StoredInvestigationRun) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

/**
 * OpenSRE-compatible investigation stream hook for Pulse.
 * Supports live SSE and offline demo simulation with Execution Trace metadata.
 */
export function useSreInvestigationStream(
  options: UseSreInvestigationStreamOptions = {}
): UseSreInvestigationStreamResult {
  const { onComplete, onError } = options;

  const [runId, setRunId] = useState<string | null>(null);
  const [status, setStatus] = useState<InvestigationRunStatus>('idle');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<InvestigationStreamMessage[]>([]);
  const [toolCalls, setToolCalls] = useState<InvestigationToolCall[]>([]);
  const [thoughts, setThoughts] = useState<InvestigationThought[]>([]);
  const [report, setReport] = useState<InvestigationReportData | null>(null);
  const [conclusionMarkdown, setConclusionMarkdown] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<Date | null>(null);
  const [completedAt, setCompletedAt] = useState<Date | null>(null);
  const [triggerSource, setTriggerSource] = useState('Pulse UI');

  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef<string | null>(null);
  const assistantMsgIdRef = useRef<string>('');
  const seqRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    runIdRef.current = null;
    seqRef.current = 0;
    setRunId(null);
    setStatus('idle');
    setIsStreaming(false);
    setError(null);
    setMessages([]);
    setToolCalls([]);
    setThoughts([]);
    setReport(null);
    setConclusionMarkdown(null);
    setStartedAt(null);
    setCompletedAt(null);
    setTriggerSource('Pulse UI');
  }, []);

  const cancel = useCallback(() => {
    const id = runIdRef.current;
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setStatus((prev) => (prev === 'running' || prev === 'queued' ? 'cancelled' : prev));
    setCompletedAt((prev) => prev ?? new Date());
    // Cooperative cancel via SRE DB — required for multi-replica; abort alone is not enough.
    if (id && getSreInvestigationMode() === 'live') {
      void cancelInvestigation(id).catch(() => {
        /* UI already shows cancelled; runner may still stop on next poll if POST retries later */
      });
    }
  }, []);

  const hydrateFromStoredRun = useCallback(
    async (runIdOrRun: string | StoredInvestigationRun) => {
      abortRef.current?.abort();
      abortRef.current = null;
      setIsStreaming(false);
      setError(null);
      setMessages([]);
      setToolCalls([]);
      setThoughts([]);
      setReport(null);
      setConclusionMarkdown(null);

      try {
        const run =
          typeof runIdOrRun === 'string'
            ? await getInvestigation(runIdOrRun)
            : runIdOrRun.report || runIdOrRun.toolCalls
              ? runIdOrRun
              : await getInvestigation(runIdOrRun.id);

        const rawStatus = (run.status || 'completed').toLowerCase();
        let nextStatus: InvestigationRunStatus = 'completed';
        if (rawStatus === 'running' || rawStatus === 'queued') nextStatus = rawStatus;
        else if (rawStatus === 'failed') nextStatus = 'failed';
        else if (rawStatus === 'cancelled') nextStatus = 'cancelled';
        else if (rawStatus === 'idle') nextStatus = 'idle';

        const mappedTools = mapStoredToolCalls(run);
        const mappedThoughts: InvestigationThought[] = Array.isArray(run.thoughts)
          ? run.thoughts.flatMap((t, index) => {
              if (!t || typeof t !== 'object') return [];
              const text = typeof t.text === 'string' ? t.text : '';
              if (!text) return [];
              return [
                {
                  text,
                  ts: typeof t.ts === 'string' ? t.ts : '',
                  seq: typeof t.seq === 'number' ? t.seq : index + 1,
                  agent: typeof t.agent === 'string' ? t.agent : undefined,
                },
              ];
            })
          : [];

        const normalizedReport = run.report
          ? normalizeInvestigationReport(
              run.report as InvestigationReportData & Record<string, unknown>
            )
          : null;

        runIdRef.current = run.id;
        setRunId(run.id);
        setStatus(nextStatus);
        setToolCalls(mappedTools);
        setThoughts(mappedThoughts);
        setReport(normalizedReport);
        setConclusionMarkdown(run.conclusionMarkdown || null);
        setStartedAt(run.startedAt ? new Date(run.startedAt) : null);
        setCompletedAt(run.completedAt ? new Date(run.completedAt) : null);
        setTriggerSource(run.triggerSource || run.triggeredBy || 'Pulse SRE');
        if (run.errorMessage) {
          setError(run.errorMessage);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load investigation';
        setError(message);
        onErrorRef.current?.(message);
      }
    },
    []
  );

  const appendThoughtText = useCallback((text: string, agent?: string, seq?: number) => {
    const id = assistantMsgIdRef.current;
    if (!text.trim()) return;
    if (id) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id
            ? { ...m, content: m.content ? `${m.content}\n${text}` : text }
            : m
        )
      );
    }
    const nextSeq = seq ?? ++seqRef.current;
    setThoughts((prev) => [
      ...prev,
      {
        text,
        ts: new Date().toISOString(),
        seq: nextSeq,
        agent,
      },
    ]);
  }, []);

  const completeWithReport = useCallback(
    (nextReport: InvestigationReportData, markdown: string) => {
      const normalized = normalizeInvestigationReport(
        nextReport as InvestigationReportData & Record<string, unknown>
      );
      setReport(normalized);
      setConclusionMarkdown(markdown);
      setStatus('completed');
      setIsStreaming(false);
      setCompletedAt(new Date());
      setToolCalls((prev) =>
        prev.map((tc) =>
          tc.status === 'running'
            ? { ...tc, status: 'completed', completedAt: new Date() }
            : tc
        )
      );
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgIdRef.current
            ? { ...m, isStreaming: false, content: m.content || markdown }
            : m
        )
      );
      onCompleteRef.current?.(normalized, markdown);
    },
    []
  );

  const fail = useCallback((message: string) => {
    setError(message);
    setStatus('failed');
    setIsStreaming(false);
    setCompletedAt(new Date());
    onErrorRef.current?.(message);
  }, []);

  const handleLiveEvent = useCallback(
    (event: Record<string, unknown>) => {
      const eventType = String(event.type || 'unknown');
      const eventData =
        event.data && typeof event.data === 'object'
          ? (event.data as Record<string, unknown>)
          : {};

      switch (eventType) {
        case SSE_EVENT.runStarted: {
          const serverRunId = String(eventData.run_id || event.run_id || '').trim();
          if (serverRunId) {
            runIdRef.current = serverRunId;
            setRunId(serverRunId);
          }
          break;
        }
        case SSE_EVENT.thought: {
          const text = String(eventData.text || event.text || '');
          if (text && text.trim() !== '(no content)') {
            appendThoughtText(
              text,
              (eventData.agent || event.agent) as string | undefined,
              typeof eventData.seq === 'number' ? eventData.seq : undefined
            );
          }
          break;
        }
        case SSE_EVENT.toolStart:
        case SSE_EVENT.toolStarted: {
          seqRef.current += 1;
          const tool: InvestigationToolCall = {
            id: String(
              eventData.tool_use_id || event.tool_use_id || `tool-${Date.now()}-${seqRef.current}`
            ),
            name: String(eventData.name || event.tool || 'unknown'),
            status: 'running',
            agentName: String(
              eventData.agent || eventData.agent_name || event.agent || 'sre-agent'
            ),
            parentAgent: eventData.parent_agent
              ? String(eventData.parent_agent)
              : undefined,
            sequenceNumber:
              typeof eventData.sequence === 'number'
                ? eventData.sequence
                : typeof eventData.seq === 'number'
                  ? eventData.seq
                  : seqRef.current,
            input: (eventData.input || event.input) as Record<string, unknown> | undefined,
            startedAt: new Date(),
          };
          setToolCalls((prev) => [...prev, tool]);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgIdRef.current
                ? { ...m, toolCalls: [...(m.toolCalls || []), tool] }
                : m
            )
          );
          break;
        }
        case SSE_EVENT.toolEnd:
        case SSE_EVENT.toolCompleted: {
          const toolUseId = String(eventData.tool_use_id || '');
          const output = String(
            eventData.output || eventData.summary || event.output_preview || ''
          );
          const success = eventData.success !== false;
          const errorMessage = eventData.error
            ? String(eventData.error)
            : undefined;
          const durationMs =
            typeof eventData.duration_ms === 'number'
              ? eventData.duration_ms
              : undefined;

          setToolCalls((prev) => {
            const patch = (tc: InvestigationToolCall): InvestigationToolCall => ({
              ...tc,
              status: success ? 'completed' : 'error',
              output,
              errorMessage,
              completedAt: new Date(),
              durationMs:
                durationMs ??
                Math.max(0, Date.now() - tc.startedAt.getTime()),
            });

            if (toolUseId) {
              return prev.map((tc) => (tc.id === toolUseId ? patch(tc) : tc));
            }
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i -= 1) {
              if (updated[i].status === 'running') {
                updated[i] = patch(updated[i]);
                break;
              }
            }
            return updated;
          });
          break;
        }
        case SSE_EVENT.report: {
          const rawReport = (eventData.report ||
            event.report ||
            eventData) as InvestigationReportData;
          const markdown = String(
            eventData.markdown || event.markdown || eventData.text || ''
          );
          if (rawReport && typeof rawReport === 'object' && 'title' in rawReport) {
            completeWithReport(rawReport, markdown);
          }
          break;
        }
        case SSE_EVENT.result:
        case SSE_EVENT.agentCompleted: {
          const status = String(eventData.status || event.status || '').toLowerCase();
          if (status === 'cancelled') {
            setStatus('cancelled');
            setIsStreaming(false);
            setCompletedAt((prev) => prev ?? new Date());
            setToolCalls((prev) =>
              prev.map((tc) =>
                tc.status === 'running'
                  ? { ...tc, status: 'error', errorMessage: 'Cancelled', completedAt: new Date() }
                  : tc
              )
            );
            break;
          }
          const structured = (eventData.structured_report ||
            event.structured_report ||
            eventData.report) as InvestigationReportData | undefined;
          const text = String(eventData.text || event.output || eventData.summary || '');
          if (structured && typeof structured === 'object') {
            completeWithReport(structured, text);
          } else if (text) {
            setConclusionMarkdown(text);
            setStatus('completed');
            setIsStreaming(false);
            setCompletedAt(new Date());
            onCompleteRef.current?.(null, text);
          }
          break;
        }
        case SSE_EVENT.error: {
          fail(String(eventData.message || event.message || 'Investigation failed'));
          break;
        }
        default:
          break;
      }
    },
    [appendThoughtText, completeWithReport, fail]
  );

  const consumeSse = useCallback(
    async (response: Response) => {
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response stream');

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = 'message';

      /** Let React paint after each event so the trace advances step-by-step. */
      const yieldToUi = (ms: number) =>
        new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            window.setTimeout(resolve, ms);
          });
        });

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n');
        buffer = chunks.pop() || '';

        for (const line of chunks) {
          const trimmed = line.trimEnd();
          if (!trimmed) continue;
          if (trimmed.startsWith('event:')) {
            currentEventType = trimmed.slice(6).trim();
            continue;
          }
          if (trimmed.startsWith('data:')) {
            const dataStr = trimmed.slice(5).trim();
            if (!dataStr || dataStr === '[DONE]') continue;
            let eventType = currentEventType;
            try {
              const parsed = JSON.parse(dataStr) as Record<string, unknown>;
              if (!parsed.type) parsed.type = currentEventType;
              eventType = String(parsed.type || currentEventType);
              handleLiveEvent(parsed);
            } catch {
              appendThoughtText(dataStr);
              eventType = 'thought';
            }
            // Pace visible steps; tool starts linger so users can follow the run
            const paceMs =
              eventType === 'tool_start' || eventType === 'tool_started'
                ? 280
                : eventType === 'thought'
                  ? 180
                  : eventType === 'tool_end' || eventType === 'tool_completed'
                    ? 120
                    : 40;
            await yieldToUi(paceMs);
          }
        }
      }
    },
    [appendThoughtText, handleLiveEvent]
  );

  const startInvestigation = useCallback(
    async (request: StartInvestigationRequest) => {
      if (isStreaming) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const nextRunId = createInvestigationRunId();
      const assistantMsgId = `assistant-${Date.now()}`;
      assistantMsgIdRef.current = assistantMsgId;
      seqRef.current = 0;
      runIdRef.current = nextRunId;

      setRunId(nextRunId);
      setStatus('running');
      setIsStreaming(true);
      setError(null);
      setReport(null);
      setConclusionMarkdown(null);
      setToolCalls([]);
      setThoughts([]);
      setStartedAt(new Date());
      setCompletedAt(null);
      const mode = getSreInvestigationMode();
      const requestTrigger = (request.triggerSource || '').trim().toLowerCase();
      setTriggerSource(
        mode === 'demo'
          ? 'Pulse UI (demo)'
          : requestTrigger === 'reinvestigate'
            ? 'Reinvestigate'
            : mode === 'live'
              ? 'Pulse UI'
              : 'system'
      );
      setMessages([
        {
          id: `user-${Date.now()}`,
          role: 'user',
          content: `Investigate ${request.ticketNumber}`,
          timestamp: new Date(),
        },
        {
          id: assistantMsgId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          toolCalls: [],
          isStreaming: true,
        },
      ]);

      try {
        if (mode === 'demo') {
          await runDemoInvestigationStream(
            {
              onThought: (thought) => {
                appendThoughtText(thought.text, thought.agent, thought.seq);
              },
              onToolStart: (payload) => {
                const tool: InvestigationToolCall = {
                  id: payload.id,
                  name: payload.name,
                  status: 'running',
                  agentName: payload.agentName,
                  parentAgent: payload.parentAgent,
                  sequenceNumber: payload.sequenceNumber,
                  input: payload.input,
                  startedAt: new Date(),
                };
                setToolCalls((prev) => [...prev, tool]);
              },
              onToolEnd: (id, output, opts) => {
                setToolCalls((prev) =>
                  prev.map((tc) =>
                    tc.id === id
                      ? {
                          ...tc,
                          status: opts?.success === false ? 'error' : 'completed',
                          output,
                          errorMessage: opts?.errorMessage,
                          completedAt: new Date(),
                          durationMs:
                            opts?.durationMs ??
                            Math.max(0, Date.now() - tc.startedAt.getTime()),
                        }
                      : tc
                  )
                );
              },
              onReport: completeWithReport,
              onError: fail,
            },
            controller.signal
          );
          return;
        }

        if (mode !== 'live') {
          fail('SRE investigation is disabled');
          return;
        }

        const response = await startLiveInvestigationStream(
          { ...request, runId: nextRunId },
          controller.signal
        );
        const headerRunId = response.headers.get('X-Investigation-Run-Id');
        if (headerRunId?.trim()) {
          runIdRef.current = headerRunId.trim();
          setRunId(headerRunId.trim());
        }
        await consumeSse(response);

        setIsStreaming(false);
        setCompletedAt((prev) => prev ?? new Date());
        setStatus((prev) => (prev === 'running' ? 'completed' : prev));
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          setStatus('cancelled');
          setIsStreaming(false);
          setCompletedAt(new Date());
          return;
        }
        fail(err instanceof Error ? err.message : 'Investigation failed');
      }
    },
    [appendThoughtText, completeWithReport, consumeSse, fail, isStreaming]
  );

  return {
    runId,
    status,
    isStreaming,
    error,
    messages,
    toolCalls,
    thoughts,
    report,
    conclusionMarkdown,
    startedAt,
    completedAt,
    triggerSource,
    startInvestigation,
    hydrateFromStoredRun,
    cancel,
    reset,
  };
}
